'use client';

/**
 * Shell layout slots for general use: aboveMain, composer, bottomBar, docViewActive.
 * Any module (e.g. editor, chat) can set these via useSlots(); shell renders them in the main-wrapper.
 * @see docs/UX_GUIDELINES.md §6, docs/mockups/themed-mockup.html
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

export type Slots = {
  aboveMain?: ReactNode;
  composer?: ReactNode;
  bottomBar?: ReactNode;
  /** When true, shell adds doc-view-active to scroll-content/scroll-area for inner doc scroll. */
  docViewActive?: boolean;
};

type SlotsContextValue = {
  slots: Slots;
  setSlots: (s: Slots | ((prev: Slots) => Slots)) => void;
};

const defaultSlots: Slots = {};
const SlotsContext = createContext<SlotsContextValue | null>(null);

const defaultContextValue: SlotsContextValue = {
  slots: defaultSlots,
  setSlots: () => {},
};

/**
 * Returns shell slots and setter. During static prerender the provider may not be in scope
 * (client boundary ordering); returns default empty slots so the shell can render without throwing.
 */
export function useSlots(): SlotsContextValue {
  const ctx = useContext(SlotsContext);
  return ctx ?? defaultContextValue;
}

export function SlotsProvider({ children }: { children: ReactNode }) {
  const [slots, setSlotsState] = useState<Slots>(defaultSlots);
  const setSlots = useCallback((s: Slots | ((prev: Slots) => Slots)) => {
    setSlotsState((prev) => (typeof s === 'function' ? s(prev) : s));
  }, []);
  return (
    <SlotsContext.Provider value={{ slots, setSlots }}>
      {children}
    </SlotsContext.Provider>
  );
}
